import { AbstractControl, FormGroup } from '@angular/forms'

/**
 * Utility class for handling form control operations.
 */
export class FormControlUtil {
    /**
     * Marks all controls in a FormGroup as touched and updates their validity.
     *
     * @param formGroup - The FormGroup whose controls should be marked as touched.
     */
    public static markFormGroupsAsTouched(formGroup: FormGroup): void {
        formGroup.updateValueAndValidity()
        formGroup.markAllAsTouched()
    }

    /**
     * This function takes a list of controls and a function to get the error for each control.
     * It aggregates all errors into a single array of strings.
     *
     * @param controls - An array of AbstractControl objects.
     * @param getErrorFn - A function that takes an AbstractControl and returns an array of string error messages.
     * @returns An array of all error messages aggregated from each control.
     */
    public static checkFormControlsForErrors(controls: AbstractControl[], getErrorFn: (control: AbstractControl) => string[]): string[] {
        return controls.reduce<string[]>((accumulatedErrors, control) => {
            const errors = getErrorFn(control)
            return accumulatedErrors.concat(errors)
        }, [])
    }

    /**
     * This function takes a list of controls and a function to get the error for each control.
     * It aggregates all errors into a single array of strings.
     *
     * @param controls - An array of AbstractControl objects.
     * @param getErrorFn - A function that takes an AbstractControl and returns an array of string error messages.
     * @returns An array of all error messages aggregated from each control.
     */
    public static checkFormControlGroupForErrors(
        controls: FormGroup,
        getErrorFn: (control: AbstractControl, controlName: string) => string[],
        markAllAsTouched = false
    ): string[] {
        if (markAllAsTouched) {
            FormControlUtil.markFormGroupsAsTouched(controls)
        }
        return Object.keys(controls.controls).reduce<string[]>((accumulatedErrors, controlName) => {
            const control = controls.controls[controlName]
            const errors = getErrorFn(control, controlName)
            return accumulatedErrors.concat(errors)
        }, [])
    }

    /**
     * Marks all controls in a FormGroup as untouched and updates their validity.
     *
     * @param formGroup - The FormGroup whose controls should be marked as untouched.
     */
    public static markFormGroupsAsUntouched(formGroup: FormGroup): void {
        formGroup.updateValueAndValidity()
        Object.values(formGroup.controls).forEach((control) => {
            control.markAsUntouched()
        })
    }
}
